import React, { useState, useEffect, useRef, useMemo } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { POKUTILS } from "../config/API";
import { withSanitizedFileName } from "../utils/sanitizeFileName";
import {
    AlertCircle,
    Building2,
    ChevronLeft,
    ChevronRight,
    Inbox,
    MessageSquare,
    MessagesSquare,
    Paperclip,
    RefreshCw,
    Search,
    Send,
    X,
} from "lucide-react";

const ThreadListSkeleton = () => (
    <div className="space-y-2 p-3">
        {[...Array(4)].map((_, i) => (
            <div key={i} className="animate-pulse rounded-xl border border-gray-100 bg-gray-50 p-4">
                <div className="flex items-start gap-3">
                    <div className="h-10 w-10 shrink-0 rounded-full bg-gray-200" />
                    <div className="flex-1 space-y-2">
                        <div className="h-4 w-3/4 rounded bg-gray-200" />
                        <div className="h-3 w-full rounded bg-gray-100" />
                        <div className="h-5 w-20 rounded-full bg-gray-100" />
                    </div>
                </div>
            </div>
        ))}
    </div>
);

const MessageSkeleton = () => (
    <div className="space-y-4 p-6">
        {[...Array(5)].map((_, i) => (
            <div
                key={i}
                className={`flex ${i % 2 === 0 ? "justify-start" : "justify-end"}`}
            >
                <div
                    className={`animate-pulse rounded-2xl bg-gray-100 ${
                        i % 2 === 0 ? "h-16 w-2/3" : "h-14 w-1/2"
                    }`}
                />
            </div>
        ))}
    </div>
);

const EmptyState = ({ icon: Icon, title, description }) => (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Icon className="h-8 w-8" strokeWidth={1.5} />
        </div>
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <p className="mt-1.5 max-w-xs text-sm leading-relaxed text-gray-500">{description}</p>
    </div>
);

const isNearBottom = (container, threshold = 100) => {
    if (!container) return true;
    const { scrollTop, scrollHeight, clientHeight } = container;
    return scrollHeight - scrollTop - clientHeight < threshold;
};

const getMessageSender = (msg) =>
    msg.NAME ?? msg.name ?? msg.sender ?? msg.from ?? "Unknown";

const getMessageIsMe = (msg, fullName) => {
    if (msg.isSender === 1 || msg.isSender === "1" || msg.isSender === true) return true;
    if (msg.isSender === 0 || msg.isSender === "0" || msg.isSender === false) return false;
    return getMessageSender(msg) === fullName;
};

const formatMessageFiles = (files) => {
    if (!Array.isArray(files)) return [];

    return files
        .map((file) => ({
            id: file.artifactid ?? file.id,
            name: file.filename ?? file.name ?? file.title ?? "Attachment",
            url: file.filelocation ?? file.url ?? file.link ?? file.filepath ?? "",
            title: file.title ?? "",
        }))
        .filter((file) => file.url);
};

const MyThread = () => {
    const [threads, setThreads] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [selectedThread, setSelectedThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [loadingThreads, setLoadingThreads] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [refreshingThreads, setRefreshingThreads] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [fullName, setFullName] = useState("");

    const chatContainerRef = useRef(null);
    const messagesIntervalRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    const scrollOnNextUpdateRef = useRef(false);

    useEffect(() => {
        const storedFullName = sessionStorage.getItem("fullname");
        setFullName(storedFullName || "");
    }, []);

    const formatMessageDate = (dateInput) => {
        try {
            if (typeof dateInput === "string") {
                return dateInput.replace(/(:\d{2})( [AP]M)/, "$2");
            }

            const date = new Date(dateInput);
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const year = date.getFullYear();
            let hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, "0");
            const ampm = hours >= 12 ? "PM" : "AM";
            hours = hours % 12 || 12;

            return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
        } catch {
            return "Just now";
        }
    };

    const getThreadId = (thread) =>
        thread?.itemid ?? thread?.threadid ?? thread?.ThreadID ?? thread?.id ?? thread?.ID;

    const getThreadTitle = (thread) =>
        thread?.name ?? thread?.Name ?? `Thread ${getThreadId(thread)}`;

    const getThreadInitials = (title) =>
        (title || "?")
            .split(" ")
            .slice(0, 2)
            .map((word) => word[0])
            .join("")
            .toUpperCase();

    const getThreadDepartment = (thread) => {
        const dept = thread?.department ?? thread?.Department ?? "";
        return dept.trim() ? dept.trim() : "Other";
    };

    const filteredThreads = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return threads;

        return threads.filter((thread) => {
            const title = getThreadTitle(thread).toLowerCase();
            const description = (thread.description || "").toLowerCase();
            const department = getThreadDepartment(thread).toLowerCase();
            return title.includes(query) || description.includes(query) || department.includes(query);
        });
    }, [threads, searchQuery]);

    const departmentGroups = useMemo(() => {
        const groups = {};

        filteredThreads.forEach((thread) => {
            const dept = getThreadDepartment(thread);
            if (!groups[dept]) {
                groups[dept] = [];
            }
            groups[dept].push(thread);
        });

        return Object.entries(groups)
            .map(([name, deptThreads]) => ({
                name,
                threads: deptThreads,
                count: deptThreads.length,
            }))
            .sort((a, b) => {
                if (a.name === "Other") return 1;
                if (b.name === "Other") return -1;
                return a.name.localeCompare(b.name);
            });
    }, [filteredThreads]);

    const departmentThreads = useMemo(() => {
        if (!selectedDepartment) return [];
        return filteredThreads.filter(
            (thread) => getThreadDepartment(thread) === selectedDepartment
        );
    }, [filteredThreads, selectedDepartment]);

    const fetchThreads = async () => {
        const params = { action: "threads" };
        try {
            const response = await fetch(POKUTILS, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    Accept: "application/json",
                },
                body: new URLSearchParams(params),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            if (Array.isArray(data)) {
                setThreads(data);
            } else if (data?.status === "error") {
                throw new Error(data.message || "Failed to load threads");
            } else {
                setThreads([]);
            }
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchThreadMessages = async (threadId, silent = false) => {
        if (!threadId) return;

        if (!silent) {
            setLoadingMessages(true);
        }

        try {
            const params = {
                action: "threadmessages",
                threadid: String(threadId),
            };
            const response = await fetch(POKUTILS, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    Accept: "application/json",
                },
                body: new URLSearchParams(params),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            if (Array.isArray(data)) {
                const formattedMessages = data.map((msg, index) => {
                    const sender = getMessageSender(msg);

                    return {
                        id: msg.MESSAGEID ?? msg.messageid ?? msg.MessageId ?? msg.id ?? Date.now() + index,
                        sender,
                        text:
                            msg.MESSAGETEXT ??
                            msg.messagetext ??
                            msg.message ??
                            msg.msg ??
                            msg.text ??
                            "",
                        time: formatMessageDate(
                            msg.MESSAGEDATE ?? msg.messagedate ?? msg.date ?? msg.time
                        ),
                        isMe: getMessageIsMe(msg, fullName),
                        avatar: sender
                            .split(", ")
                            .map((part) => part[0])
                            .join(""),
                        files: formatMessageFiles(msg.files),
                    };
                });

                scrollOnNextUpdateRef.current =
                    !silent || isNearBottom(chatContainerRef.current);
                setMessages(formattedMessages.reverse());
            } else if (data?.status === "error") {
                throw new Error(data.message || "Failed to load messages");
            } else {
                scrollOnNextUpdateRef.current = !silent;
                setMessages([]);
            }
        } catch (err) {
            if (!silent) {
                setError(err.message);
            }
        } finally {
            if (!silent) {
                setLoadingMessages(false);
            }
        }
    };

    useEffect(() => {
        const loadThreads = async () => {
            setLoadingThreads(true);
            setError(null);
            try {
                await fetchThreads();
            } finally {
                setLoadingThreads(false);
            }
        };

        loadThreads();
    }, []);

    useEffect(() => {
        if (!selectedThread) {
            if (messagesIntervalRef.current) {
                clearInterval(messagesIntervalRef.current);
                messagesIntervalRef.current = null;
            }
            return;
        }

        const threadId = getThreadId(selectedThread);
        setError(null);
        fetchThreadMessages(threadId);

        messagesIntervalRef.current = setInterval(() => {
            fetchThreadMessages(threadId, true);
        }, 5000);

        return () => {
            if (messagesIntervalRef.current) {
                clearInterval(messagesIntervalRef.current);
            }
        };
    }, [selectedThread, fullName]);

    useEffect(() => {
        if (!scrollOnNextUpdateRef.current || !chatContainerRef.current) return;

        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        scrollOnNextUpdateRef.current = false;
    }, [messages]);

    const handleRefreshThreads = async () => {
        setRefreshingThreads(true);
        setError(null);
        try {
            await fetchThreads();
        } finally {
            setRefreshingThreads(false);
        }
    };

    const clearComposer = () => {
        setNewMessage("");
        setAttachedFiles([]);
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            setAttachedFiles((prev) => [...prev, ...files]);
        }
        e.target.value = "";
    };

    const handleRemoveFile = (index) => {
        setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSelectDepartment = (department) => {
        setSelectedDepartment(department);
        setSelectedThread(null);
        setMessages([]);
        clearComposer();
        setError(null);
    };

    const handleSelectThread = (thread) => {
        setSelectedThread(thread);
        setMessages([]);
        clearComposer();
        setError(null);
    };

    const handleBackToThreads = () => {
        setSelectedThread(null);
        setMessages([]);
        clearComposer();
        setError(null);
    };

    const handleBackToDepartments = () => {
        setSelectedDepartment(null);
        setSelectedThread(null);
        setMessages([]);
        clearComposer();
        setError(null);
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        const trimmed = newMessage.trim();
        if ((!trimmed && attachedFiles.length === 0) || !selectedThread || sending) return;

        const threadId = getThreadId(selectedThread);
        const tempId = Date.now();
        const filesToSend = [...attachedFiles];
        const optimisticMessage = {
            id: tempId,
            sender: "You",
            text: trimmed,
            time: formatMessageDate(new Date()),
            isMe: true,
            files: filesToSend.map((file, index) => ({
                id: `temp-${tempId}-${index}`,
                name: file.name,
                url: "",
                title: "",
            })),
        };

        scrollOnNextUpdateRef.current = true;
        setMessages((prev) => [...prev, optimisticMessage]);
        clearComposer();
        setSending(true);
        setError(null);

        try {
            let response;
            const requestUrl = POKUTILS;

            if (filesToSend.length > 0) {
                const formData = new FormData();
                formData.append("action", "sendthreadmessage");
                formData.append("threadid", String(threadId));
                formData.append("msg", trimmed);
                filesToSend.forEach((file) => {
                    const safeFile = withSanitizedFileName(file);
                    formData.append("file", safeFile, safeFile.name);
                });

                const requestParams = {
                    action: "sendthreadmessage",
                    threadid: String(threadId),
                    msg: trimmed,
                    file: filesToSend.map((file) => ({
                        name: file.name,
                        size: file.size,
                        type: file.type,
                    })),
                };

                console.log("Send message API URL:", requestUrl);
                console.log("Send message API params:", requestParams);

                response = await fetch(requestUrl, {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                    },
                    body: formData,
                });
            } else {
                const requestParams = {
                    action: "sendthreadmessage",
                    threadid: String(threadId),
                    msg: trimmed,
                };

                console.log("Send message API URL:", requestUrl);
                console.log("Send message API params:", requestParams);

                response = await fetch(requestUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        Accept: "application/json",
                    },
                    body: new URLSearchParams(requestParams),
                });
            }

            const result = await response.json();
            console.log("Send message API response:", result);

            if (!response.ok) {
                throw new Error("Failed to send message");
            }
            if (result?.status === "error") {
                throw new Error(result.message || "Failed to send message");
            }

            setMessages((prev) =>
                prev.map((msg) =>
                    msg.id === tempId ? { ...msg, id: result.id ?? tempId } : msg
                )
            );

            await fetchThreadMessages(threadId, true);
        } catch (err) {
            setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
            setNewMessage(trimmed);
            setAttachedFiles(filesToSend);
            setError(err.message || "Failed to send message. Please try again.");
        } finally {
            setSending(false);
            inputRef.current?.focus();
        }
    };

    const renderDepartmentList = () => (
        <ul className="space-y-3">
            {departmentGroups.map((group) => (
                <li key={group.name}>
                    <button
                        type="button"
                        onClick={() => handleSelectDepartment(group.name)}
                        className="group w-full rounded-2xl border border-gray-100 bg-white p-4 text-left shadow-sm transition-all duration-200 hover:border-primary/25 hover:shadow-md"
                    >
                        <div className="flex items-center gap-4">
                            <div className="relative shrink-0">
                                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 via-primary/10 to-primary/5 text-primary transition group-hover:from-primary/20 group-hover:to-primary/10">
                                    <Building2 className="h-5 w-5" strokeWidth={2} />
                                </div>
                                {group.count > 0 && (
                                    <span className="absolute -right-1 -top-1 flex h-[22px] min-w-[22px] items-center justify-center rounded-full border-2 border-white bg-red-500 px-1 text-[10px] font-bold leading-none text-white shadow-md">
                                        {group.count > 99 ? "99+" : group.count}
                                    </span>
                                )}
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-[15px] font-semibold text-gray-900 transition group-hover:text-primary">
                                    {group.name}
                                </p>
                                <p className="mt-1 text-xs text-gray-500">
                                    {group.count === 1 ? "1 conversation" : `${group.count} conversations`}
                                </p>
                            </div>
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-50 text-gray-400 transition group-hover:bg-primary/10 group-hover:text-primary">
                                <ChevronRight className="h-4 w-4" />
                            </div>
                        </div>
                    </button>
                </li>
            ))}
        </ul>
    );

    const renderThreadItems = (threadList) => (
        <ul className="space-y-2">
            {threadList.map((thread) => {
                const threadId = getThreadId(thread);
                const title = getThreadTitle(thread);
                const isSelected = getThreadId(selectedThread) === threadId;

                return (
                    <li key={threadId}>
                        <button
                            type="button"
                            onClick={() => handleSelectThread(thread)}
                            className={`group w-full rounded-xl border p-4 text-left transition-all ${
                                isSelected
                                    ? "border-primary/30 bg-primary/5 shadow-sm ring-1 ring-primary/20"
                                    : "border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50/80 hover:shadow-sm"
                            }`}
                        >
                            <div className="flex items-start gap-3">
                                <div
                                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                                        isSelected
                                            ? "bg-primary text-white"
                                            : "bg-primary/10 text-primary group-hover:bg-primary/15"
                                    }`}
                                >
                                    {getThreadInitials(title)}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="truncate font-semibold text-gray-900">{title}</p>
                                    {thread.description && (
                                        <p className="mt-1 line-clamp-2 text-sm leading-snug text-gray-500">
                                            {thread.description}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </button>
                    </li>
                );
            })}
        </ul>
    );

    const renderThreadList = () => {
        const isDepartmentView = !selectedDepartment;
        const listTitle = isDepartmentView ? "My Threads" : selectedDepartment;
        const listSubtitle = isDepartmentView
            ? `${departmentGroups.length} ${departmentGroups.length === 1 ? "department" : "departments"}`
            : `${departmentThreads.length} ${departmentThreads.length === 1 ? "conversation" : "conversations"}`;
        const showDepartmentList = isDepartmentView;
        const activeThreadList = isDepartmentView ? filteredThreads : departmentThreads;
        const isEmpty = showDepartmentList
            ? departmentGroups.length === 0
            : activeThreadList.length === 0;

        return (
        <div className="flex h-full flex-col bg-white">
            <div className="relative border-b border-gray-100 bg-gradient-to-r from-primary/5 via-white to-white px-4 py-4 sm:px-5">
                <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary via-primary/80 to-primary/40" />
                <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                        {!isDepartmentView && (
                            <button
                                type="button"
                                onClick={handleBackToDepartments}
                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
                                aria-label="Back to departments"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </button>
                        )}
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            {isDepartmentView ? (
                                <MessagesSquare className="h-4 w-4" strokeWidth={2} />
                            ) : (
                                <Building2 className="h-4 w-4" strokeWidth={2} />
                            )}
                        </div>
                        <div className="min-w-0">
                            <h2 className="truncate text-base font-bold tracking-tight text-gray-900 sm:text-lg">
                                {listTitle}
                            </h2>
                            <p className="text-xs text-gray-500 sm:text-sm">{listSubtitle}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleRefreshThreads}
                        disabled={refreshingThreads || loadingThreads}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 shadow-sm transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Refresh threads"
                    >
                        <RefreshCw
                            className={`h-4 w-4 ${refreshingThreads ? "animate-spin" : ""}`}
                        />
                    </button>
                </div>

                <div className="relative mt-4">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={
                            isDepartmentView
                                ? "Search departments and threads..."
                                : "Search threads..."
                        }
                        className="w-full rounded-xl border border-gray-200 bg-gray-50/80 py-2.5 pl-10 pr-4 text-sm text-gray-700 placeholder-gray-400 transition focus:border-primary/30 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/10"
                    />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
                {loadingThreads ? (
                    <ThreadListSkeleton />
                ) : isEmpty ? (
                    <EmptyState
                        icon={searchQuery ? Search : Inbox}
                        title={
                            searchQuery
                                ? "No matching results"
                                : isDepartmentView
                                  ? "No threads yet"
                                  : "No threads in this department"
                        }
                        description={
                            searchQuery
                                ? "Try a different search term or clear the filter."
                                : isDepartmentView
                                  ? "When threads are assigned to you, they will appear here."
                                  : "There are no conversations in this department yet."
                        }
                    />
                ) : showDepartmentList ? (
                    renderDepartmentList()
                ) : (
                    renderThreadItems(activeThreadList)
                )}
            </div>
        </div>
        );
    };

    const renderConversation = () => {
        if (!selectedThread) {
            return (
                <div className="hidden flex-1 flex-col items-center justify-center bg-gradient-to-b from-gray-50/50 to-white md:flex">
                    <EmptyState
                        icon={MessageSquare}
                        title="Select a thread"
                        description="Choose a department, then select a conversation to view messages and reply."
                    />
                </div>
            );
        }

        const threadTitle = getThreadTitle(selectedThread);

        return (
            <div className="flex h-full flex-col bg-white">
                <div className="relative border-b border-gray-100 bg-gradient-to-r from-white via-white to-primary/5 px-4 py-4 sm:px-5">
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={handleBackToThreads}
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary md:hidden"
                            aria-label="Back to threads"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>

                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">
                            {getThreadInitials(threadTitle)}
                        </div>

                        <div className="min-w-0 flex-1">
                            <h2 className="truncate text-base font-bold tracking-tight text-gray-900 sm:text-lg">
                                {threadTitle}
                            </h2>
                            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-500 sm:text-sm">
                                {selectedThread.description && (
                                    <span className="truncate">{selectedThread.description}</span>
                                )}
                                {selectedThread.department && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">
                                        <Building2 className="h-3 w-3" />
                                        {selectedThread.department}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    ref={chatContainerRef}
                    className="flex-1 overflow-y-auto bg-[linear-gradient(to_bottom,#f9fafb_0%,#ffffff_100%)] px-4 py-5 sm:px-6"
                >
                    {error && (
                        <div className="mb-4 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                            <div className="flex-1">
                                <p className="font-medium">Something went wrong</p>
                                <p className="mt-0.5 text-red-600/90">{error}</p>
                            </div>
                        </div>
                    )}

                    {loadingMessages ? (
                        <MessageSkeleton />
                    ) : messages.length === 0 ? (
                        <EmptyState
                            icon={MessageSquare}
                            title="No messages yet"
                            description="Start the conversation by sending a message below."
                        />
                    ) : (
                        <div className="space-y-4">
                            {messages.map((message) => (
                                <div
                                    key={message.id}
                                    className={`flex ${message.isMe ? "justify-end" : "justify-start"} items-end gap-2.5`}
                                >
                                    {!message.isMe && (
                                        <div className="mb-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                                            {message.avatar}
                                        </div>
                                    )}
                                    <div
                                        className={`max-w-[min(85%,28rem)] rounded-2xl px-4 py-3 shadow-sm ${
                                            message.isMe
                                                ? "rounded-br-md bg-primary text-white"
                                                : "rounded-bl-md border border-gray-100 bg-white text-gray-800"
                                        }`}
                                    >
                                        {!message.isMe && (
                                            <p className="mb-1 text-xs font-semibold text-primary">
                                                {message.sender}
                                            </p>
                                        )}
                                        {message.text && (
                                            <p
                                                className={`whitespace-pre-wrap text-sm leading-relaxed ${
                                                    message.isMe ? "text-white" : "text-gray-700"
                                                }`}
                                            >
                                                {message.text}
                                            </p>
                                        )}
                                        {message.files?.length > 0 && (
                                            <div
                                                className={`space-y-1.5 ${
                                                    message.text ? "mt-2" : ""
                                                }`}
                                            >
                                                {message.files.map((file) => {
                                                    const fileClassName = `flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium transition ${
                                                        message.isMe
                                                            ? "bg-white/15 text-white hover:bg-white/25"
                                                            : "border border-gray-100 bg-gray-50 text-primary hover:bg-primary/5"
                                                    }`;

                                                    if (file.url) {
                                                        return (
                                                            <a
                                                                key={file.id || file.url}
                                                                href={file.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className={fileClassName}
                                                            >
                                                                <Paperclip className="h-3.5 w-3.5 shrink-0" />
                                                                <span className="truncate">{file.name}</span>
                                                            </a>
                                                        );
                                                    }

                                                    return (
                                                        <div key={file.id || file.name} className={fileClassName}>
                                                            <Paperclip className="h-3.5 w-3.5 shrink-0" />
                                                            <span className="truncate">{file.name}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        <p
                                            className={`mt-1.5 text-[11px] ${
                                                message.isMe ? "text-primary-100/90" : "text-gray-400"
                                            }`}
                                        >
                                            {message.time}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <form
                    onSubmit={handleSendMessage}
                    className="border-t border-gray-100 bg-white px-4 py-4 sm:px-5"
                >
                    {attachedFiles.length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-2">
                            {attachedFiles.map((file, index) => (
                                <div
                                    key={`${file.name}-${file.lastModified}-${index}`}
                                    className="flex max-w-full items-center gap-1.5 rounded-lg border border-primary/15 bg-primary/5 px-2.5 py-1.5 text-xs text-primary"
                                >
                                    <Paperclip className="h-3.5 w-3.5 shrink-0" />
                                    <span className="max-w-[160px] truncate">{file.name}</span>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveFile(index)}
                                        disabled={sending}
                                        className="rounded p-0.5 transition hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
                                        aria-label={`Remove ${file.name}`}
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex items-end gap-2 rounded-2xl border border-gray-200 bg-gray-50/60 p-2 shadow-sm transition focus-within:border-primary/30 focus-within:bg-white focus-within:ring-2 focus-within:ring-primary/10">
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            className="hidden"
                            onChange={handleFileSelect}
                            disabled={sending}
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={sending}
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
                            aria-label="Attach files"
                        >
                            <Paperclip className="h-4 w-4" />
                        </button>
                        <input
                            ref={inputRef}
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Write your reply..."
                            disabled={sending}
                            className="min-h-[44px] flex-1 bg-transparent px-1 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none disabled:cursor-not-allowed sm:px-2"
                        />
                        <button
                            type="submit"
                            disabled={
                                sending || (!newMessage.trim() && attachedFiles.length === 0)
                            }
                            className="flex h-11 shrink-0 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-medium text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 sm:px-5"
                        >
                            {sending ? (
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            ) : (
                                <>
                                    <Send className="h-4 w-4" />
                                    <span className="hidden sm:inline">Send</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        );
    };

    return (
        <div className="flex h-screen flex-col bg-gray-50">
            <div className="fixed left-0 top-0 z-50 w-full bg-primary shadow-sm">
                <Header />
            </div>

            <div className="mt-[72px] flex flex-1">
                <div className="fixed left-0 hidden h-[calc(100vh-72px)] w-64 overflow-y-auto border-r border-gray-200 bg-white lg:block">
                    <Sidebar />
                </div>

                <main className="mt-[25px] flex-1 overflow-hidden p-4 sm:p-6 lg:ml-64">
                    <div className="mx-auto w-full max-w-6xl">
                        <div className="mb-4 hidden sm:block">
                            <h1 className="text-xl font-bold tracking-tight text-gray-900">
                                Thread Messages
                            </h1>
                            <p className="mt-1 text-sm text-gray-500">
                                View and respond to your department conversations
                            </p>
                        </div>

                        <div
                            className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm"
                            style={{ height: "calc(100vh - 200px)" }}
                        >
                            <div className="flex h-full">
                                <div
                                    className={`w-full shrink-0 border-r border-gray-100 md:w-[340px] lg:w-[380px] ${
                                        selectedThread ? "hidden md:flex md:flex-col" : "flex flex-col"
                                    }`}
                                >
                                    {renderThreadList()}
                                </div>

                                <div
                                    className={`min-w-0 flex-1 ${
                                        selectedThread ? "flex flex-col" : "hidden md:flex md:flex-col"
                                    }`}
                                >
                                    {renderConversation()}
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default MyThread;
